// Copyright (c) 2023 embyt GmbH. All rights reserved.
// Author: Roman Morawek / Marc Planeck

import { connect } from "react-redux";
import { ChartData, ChartOptions, ScatterDataPoint } from "chart.js";

import { getTrendIoGraphViewMode, getColor, getIoType, getSiteSelection } from "guiutils";
import { IState } from "states/states";
import { GraphViewMode } from "states/cachedstates";
import { IIoTrendCount } from "states/trendingstates";

import IoGraphBaseComponent, {
  IoChartType,
  mapBaseStateToProps,
  mapDispatchToProps,
} from "./IoGraphBase";

import cssContent from "constants.module.scss";

interface IIoGraphDigitalCountProps {
  ioTrendCount: IIoTrendCount;
  lastUpdate: number | undefined;
  graphViewMode: GraphViewMode;
}

const mapStateToProps = (state: IState) => {
  const props: IIoGraphDigitalCountProps = {
    ioTrendCount: state.trending.ioTrendCount,
    lastUpdate: state.scheduler.ioTrendLoading.lastData, // to ensure screen update
    graphViewMode:
      getTrendIoGraphViewMode(
        getSiteSelection(state),
        getIoType(state),
        Object.keys(state.trending.ioTrendFull).length > 0,
        true,
      ) ?? GraphViewMode.BAR,
  };
  return { ...mapBaseStateToProps(state), ...props };
};

// aggregated digital IOs: switching counts as stacked bars or lines
class IoGraphDigitalCountComponent extends IoGraphBaseComponent<IIoGraphDigitalCountProps> {
  protected readonly ioType = "digital";

  private maxValue = 0; // sum of all visible IOs, for stacked bars
  private singleMaxValue = 0; // maximum of a single IO, for lines

  protected getChartType(): IoChartType {
    return this.props.graphViewMode === GraphViewMode.LINE ? "line" : "bar";
  }

  protected preProcessData() {
    const oldMaxValue = this.maxValue;
    const oldSingleMaxValue = this.singleMaxValue;
    this.maxValue = 0;
    this.singleMaxValue = 0;

    const startTime = this.props.graphStart!;
    const endTime = this.props.graphEnd!;
    const timestamps = Object.keys(this.props.ioTrendCount)
      .map((item) => parseInt(item))
      .filter((ts) => ts >= startTime && ts < endTime);

    this.data = this.selectedIos.map((selectedIo) => {
      const sourceIndex = selectedIo.source === "input" ? 0 : 1;
      const values = timestamps.map((ts) => {
        const count = this.props.ioTrendCount[ts]?.[sourceIndex]?.[selectedIo.index] ?? 0;
        return [ts, isNaN(count) ? 0 : count];
      });

      if (selectedIo.visible === true) {
        const curMax = Math.max(0, ...values.map((value) => value[1]));
        this.maxValue += curMax;
        this.singleMaxValue = Math.max(this.singleMaxValue, curMax);
      }
      return values;
    });

    // keep the previous scaling if there is no data
    if (this.maxValue === 0) {
      this.maxValue = oldMaxValue;
    }
    if (this.singleMaxValue === 0) {
      this.singleMaxValue = oldSingleMaxValue;
    }
    // put 5% overhead to not put the max on the top-point of the graph view
    this.maxValue = Math.round(this.maxValue * 1.05);
    this.singleMaxValue = Math.round(this.singleMaxValue * 1.05);
  }

  protected createGraphOptions() {
    const isBar = this.props.graphViewMode === GraphViewMode.BAR;
    const maxY = isBar ? this.maxValue : this.singleMaxValue;
    const isLegendTop = this.isLegendTop();
    const options: ChartOptions<IoChartType> = {
      scales: {
        x: this.createTimeScale(),
        y: {
          type: "linear",
          max: maxY,
          ticks: {
            stepSize: maxY % 500,
          },
          beginAtZero: true,
          display: true,
          stacked: isBar,
        },
      },
      animation: {
        duration: 0,
      },
      maintainAspectRatio: false, // needed to fit size in status screen
      elements: {
        point: {
          radius: 2,
        },
      },
      datasets: {
        line: {
          tension: 0.2, // line smoothness, default 0
        },
      },
      plugins: {
        legend: {
          display: window.innerWidth > parseInt(cssContent.GRID_BREAKPOINT_MD) || isLegendTop,
          position: isLegendTop ? "top" : "right",
          onClick: (evt, legendItem) => this.legendItemClick(evt, legendItem),
        },
        tooltip: {
          enabled: true,
          mode: "point", // use "index" to show all data items on same x value
          position: "nearest", // show tooltip on average value, not at mouse cursor
          intersect: true,
          callbacks: {
            title: (tooltipItems) => this.createTooltipTitle(tooltipItems),
            label: (tooltipItem) => {
              // do we face a background fill data set?
              if (tooltipItem.dataset.label === undefined) {
                return undefined as unknown as string; // we need to disable the wrong type check here
              }
              return ` ${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
            },
          },
        },
        zoom: this.createZoomOptions(),
        datalabels: {
          display: false,
        },
      },
    };
    return options;
  }

  protected createGraphData() {
    const data: ChartData<IoChartType, ScatterDataPoint[], unknown> = {
      datasets: [],
    };
    this.data.forEach((dataset, anIndex) => {
      const selectedIo = this.selectedIos[anIndex];
      const color = getColor(selectedIo?.index ?? 0);
      data.datasets.push({
        label: this.parameter[anIndex]?.name,
        data:
          selectedIo?.visible === true ?
            dataset.map((curItem) => ({
              x: curItem[0] * 1000, // timestamp
              y: curItem[1],
            }))
          : [],
        type: this.getChartType(),
        hidden: selectedIo?.visible === false,
        borderColor: color,
        backgroundColor: color,
      });
    });
    return data;
  }
}

const IoGraphDigitalCount = connect(
  mapStateToProps,
  mapDispatchToProps,
)(IoGraphDigitalCountComponent);

export default IoGraphDigitalCount;
